import React, { useEffect, useState } from 'react';
import { FaInstagram, FaFacebook, FaGlobe, FaYoutube, FaMapMarkerAlt, FaPhoneAlt, FaEnvelope } from 'react-icons/fa';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';

const Footer = () => {
  const [footerData, setFooterData] = useState(null);
  const { language } = useLanguage(); // Access the selected language

  useEffect(() => {
    const fetchFooterData = async () => {
      try {
        const response = await axios.get('https://annexpilot.com/dashboard/wp-json/wp/v2/homepage');
        const data = response.data.find((item) =>
          item.page_language.some((lang) => lang.slug === (language === 'English' ? 'en' : 'sq')) ||
          item.page_language.some((lang) => lang.slug === 'sq') // Fallback to default
        );
        setFooterData(data);
      } catch (error) {
        console.error('Error fetching footer data:', error);
      }
    };

    fetchFooterData();
  }, [language]);

  if (!footerData) {
    return <p>{language === 'English' ? 'Loading...' : 'Duke u ngarkuar...'}</p>;
  }

  const {
    instagram_link,
    facebook_link,
    website_link,
    youtube_link,
    map_address,
    phone_number,
    email,
    disclaimer_text,
    supported_by_logos,
    program_by_logo,
  } = footerData;

  return (
    <footer className="footer relative bg-white text-surface dark:text-black">
      <div className="footer-background"></div>
      <div className="border-t-4 border-gray-200 shadow-md"></div>

      <div className="max-w-7xl mx-auto py-9 px-8 relative z-10">
        {/* Logo Section */}
        <div className="flex justify-center items-center flex-wrap gap-8 mb-8 text-center">
          {/* Program By Section */}
          <p className="font-bold mb-2">
            {language === 'English' ? 'Annex is a program by:' : 'Annex është një program nga:'}
          </p>
          {program_by_logo && (
            <img
              src={program_by_logo.guid}
              alt={program_by_logo.post_title || 'Program by logo'}
              className="h-12 w-auto"
            />
          )}

          {/* Supported By Section */}
          <p className="font-bold mb-2">
            {language === 'English' ? 'Supported by:' : 'Mbështetur nga:'}
          </p>
          {supported_by_logos &&
            supported_by_logos.map((logo, index) => (
              <img
                key={index}
                src={logo.guid}
                alt={logo.post_title || 'Supported by logo'}
                className="h-12 w-auto"
              />
            ))}
        </div>

        {/* Disclaimer Section */}
        <div className="p-4 my-4 max-w-4xl mx-auto text-center">
          {disclaimer_text ? (
            <div
              dangerouslySetInnerHTML={{ __html: disclaimer_text }}
              className="font-bold text-md text-gray-1000"
            ></div>
          ) : (
            <p className="font-bold text-md text-gray-1000">
              {language === 'English'
                ? 'Disclaimer not available.'
                : 'Kjo deklaratë nuk është e disponueshme.'}
            </p>
          )}
        </div>


        {/* Media and Contacts Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 text-center">
          {/* Media Section */}
          <div className="mb-6">
            <h2 className="font-bold mb-2.5">{language === 'English' ? 'Media' : 'Media'}</h2>
            <ul className="list-none flex justify-center space-x-6">
              {instagram_link && (
                <li>
                  <a
                    href={instagram_link}
                    className="hover:underline flex items-center justify-center"
                    aria-label={language === 'English' ? 'Visit Instagram Profile' : 'Vizitoni Profilin Instagram'}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <FaInstagram className="text-3xl" />
                  </a>
                </li>
              )}
              {facebook_link && (
                <li>
                  <a
                    href={facebook_link}
                    className="hover:underline flex items-center justify-center"
                    aria-label={language === 'English' ? 'Visit Facebook Profile' : 'Vizitoni Profilin Facebook'}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <FaFacebook className="text-3xl" />
                  </a>
                </li>
              )}
              {website_link && (
                <li>
                  <a
                    href={website_link}
                    className="hover:underline flex items-center justify-center"
                    aria-label={language === 'English' ? 'Visit Website' : 'Vizitoni Faqen e Internetit'}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <FaGlobe className="text-3xl" />
                  </a>
                </li>
              )}
              {youtube_link && (
                <li>
                  <a
                    href={youtube_link}
                    className="hover:underline flex items-center justify-center"
                    aria-label={language === 'English' ? 'Visit Youtube Channel' : 'Vizitoni Kanali Youtube'}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <FaYoutube className="text-3xl" />
                  </a>
                </li>
              )}
            </ul>
          </div>

          {/* Contacts Section */}
          <div className="mb-6">
            <h2 className="font-bold mb-2.5">{language === 'English' ? 'Contacts' : 'Kontaktet'}</h2>
            <ul className="list-none flex justify-center space-x-6">
              {map_address && (
                <li>
                  <a
                    href={map_address}
                    className="hover:underline flex items-center justify-center"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={language === 'English' ? 'Our Location' : 'Vendndodhja jonë'}
                  >
                    <FaMapMarkerAlt className="text-3xl" />
                  </a>
                </li>
              )}
              {phone_number && (
                <li>
                  <a
                    href={`tel:${phone_number}`}
                    className="hover:underline flex items-center justify-center"
                    aria-label={language === 'English' ? 'Call us' : 'Na telefononi'}
                  >
                    <FaPhoneAlt className="text-3xl" />
                  </a>
                </li>
              )}
              {email && (
                <li>
                  <a
                    href={`mailto:${email}`}
                    className="hover:underline flex items-center justify-center"
                    aria-label={language === 'English' ? 'Email us' : 'Na kontaktoni me email'}
                  >
                    <FaEnvelope className="text-3xl" />
                  </a>
                </li>
              )}
            </ul>
          </div>
        </div>

        {/* Accessibility Statement Link */}
        {footerData.accessibility_statement && (
          <div className="mt-8 text-center">
            <a
              href={footerData.accessibility_statement.guid}
              className="text-primary hover:underline font-bold text-md"
              target="_blank"
              rel="noopener noreferrer"
              aria-label={
                language === 'English'
                  ? 'Read our Accessibility Statement in a new tab'
                  : 'Lexoni Deklaratën tonë për Aksesueshmërinë në një skedë të re'
              }
            >
              {language === 'English' ? 'Accessibility Statement' : 'Deklarata për Aksesueshmërinë'}
            </a>
          </div>
        )}
      </div>
    </footer>
  );
};

export default Footer;